import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import BlockEditor from '../../components/b2b/BlockEditor'
import ConsentCheckboxes from '../../components/ConsentCheckboxes'
import { api, APIError } from '../../api/client'
import { LEGAL_VERSIONS } from '../../config/legalVersions'

const DEFAULT_RESUME_BLOCKS = [
  { id: 'preset-summary', label: 'Summary', content: '' },
  { id: 'preset-experience', label: 'Experience', content: '' },
  { id: 'preset-skills', label: 'Skills', content: '' },
]

export default function AddCandidateScreen() {
  const navigate = useNavigate()
  const { jobId } = useParams()

  const [job, setJob] = useState(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [resumeBlocks, setResumeBlocks] = useState(DEFAULT_RESUME_BLOCKS)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [consent, setConsent] = useState({ lawful_basis_confirmed: false })
  const updateConsent = (key, value) => setConsent((prev) => ({ ...prev, [key]: value }))

  useEffect(() => {
    let cancelled = false
    api.get(`/api/b2b/jobs/${jobId}`)
      .then((data) => { if (!cancelled) setJob(data) })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof APIError && err.status === 404) setError('Job not found.')
        else setError('Could not load job.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [jobId])

  const handleSave = async () => {
    setError('')
    if (!name.trim()) {
      setError('Candidate name is required.')
      return
    }

    if (!consent.lawful_basis_confirmed) {
      setError('Please confirm you have a lawful basis to process this candidate’s data.')
      return
    }

    setSaving(true)
    try {
      const created = await api.post(`/api/b2b/jobs/${jobId}/candidates`, {
        name: name.trim(),
        email: email.trim() || null,
        resume_blocks: resumeBlocks.map((b, idx) => ({
          label: b.label.trim() || `Section ${idx + 1}`,
          content: b.content || '',
          position: idx,
        })),
        lawful_basis_attestation: {
          version: LEGAL_VERSIONS.LAWFUL_BASIS,
          confirmed: true,
        },
      })
      navigate(`/b2b/jobs/${jobId}/candidates/${created.id}`)
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not save candidate.')
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-3xl mx-auto">
          <p className="text-sm text-ink-3">Loading…</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl mx-auto">
        <Link to={`/b2b/jobs/${jobId}`} className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
          <ArrowLeft className="w-4 h-4" />
          Back to {job?.title || 'job'}
        </Link>

        <h1 className="mt-4 text-2xl md:text-3xl font-bold text-ink-1">Add candidate</h1>
        <p className="text-sm text-ink-3 mt-1">
          Paste the candidate's resume sections in any structure. You'll generate screening questions next.
        </p>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="mt-8 space-y-4">
          <Field label="Full name">
            <input
              type="text"
              placeholder="e.g., Mira Chen"
              className="flex-1 text-[15px] outline-none bg-transparent"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
            />
          </Field>

          <Field label="Email" hint="Used for sending the screening link.">
            <input
              type="email"
              placeholder="candidate@example.com"
              className="flex-1 text-[15px] outline-none bg-transparent"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={saving}
            />
          </Field>
        </div>

        <div className="mt-10">
          <h2 className="text-sm font-bold uppercase tracking-wider text-ink-3 mb-4">
            Resume
          </h2>
          <BlockEditor
            blocks={resumeBlocks}
            onChange={setResumeBlocks}
            addLabel="Add section"
            contentPlaceholder="Paste content from CV here…"
          />
        </div>

        <div className="mt-10">
          <ConsentCheckboxes mode="addCandidate" values={consent} onChange={updateConsent} />
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/b2b/jobs/${jobId}`)}
            disabled={saving}
            className="px-5 py-2.5 rounded-2xl text-sm font-semibold text-ink-2 hover:bg-canvas transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save candidate'}
          </button>
        </div>
      </div>
    </AppShell>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">{label}</label>
      <div className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3.5 focus-within:border-accent transition-colors">
        {children}
      </div>
      {hint && <p className="text-[11px] text-ink-3 ml-1">{hint}</p>}
    </div>
  )
}
